import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Unlink, Link2 } from "lucide-react";
import { api } from "@/lib/api";
import { ROLE_LABELS } from "./userUtils";

function OrphanRow({ row, onLinked }) {
  const [pid, setPid] = useState(row.suggested_id || "");
  const [busy, setBusy] = useState(false);
  const [invite, setInvite] = useState(false);
  const [q, setQ] = useState("");
  const norm = (s) => (s || "").toLocaleLowerCase("tr-TR");
  const shown = row.candidates.filter((c) => c.id === pid || !q || norm(`${c.ad_soyad} ${c.pozisyon}`).includes(norm(q)));
  const link = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/users/${row.id}/relink`, { personnel_id: pid, send_invite: invite });
      toast.success(`${row.email} → ${r.data.ad_soyad} bağlandı${r.data.invite ? " · davet e-postası gönderildi" : ""}`);
      onLinked();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bağlanamadı");
    } finally { setBusy(false); }
  };
  return (
    <div data-testid={`orphan-row-${row.email}`} className="flex flex-col md:flex-row md:items-center gap-2 py-2 border-t border-rose-100 first:border-t-0">
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-900 truncate">{row.name || row.email}</div>
        <div className="text-[11px] text-slate-500 truncate">{row.email} · {ROLE_LABELS[row.role] || row.role}</div>
      </div>
      <input data-testid={`orphan-search-${row.email}`} value={q} placeholder="Personel ara…"
        onChange={(e) => {
          const v = e.target.value; setQ(v);
          const hit = row.candidates.filter((c) => norm(c.ad_soyad).includes(norm(v)));
          if (v && hit.length === 1) setPid(hit[0].id);
        }}
        className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white md:w-40" />
      <select data-testid={`orphan-select-${row.email}`} value={pid} onChange={(e) => setPid(e.target.value)}
        className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white md:w-[26rem]">
        <option value="">Personel seçin…</option>
        {shown.map((c) => (
          <option key={c.id} value={c.id}>
            {c.ad_soyad}{c.pozisyon ? ` · ${c.pozisyon}` : ""}{c.score >= 0.75 ? ` (benzerlik %${Math.round(c.score * 100)})` : ""}
          </option>
        ))}
      </select>
      <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-700 cursor-pointer whitespace-nowrap" title="Geçici şifreyle davet e-postası gönder">
        <input type="checkbox" data-testid={`orphan-invite-${row.email}`} checked={invite} onChange={(e) => setInvite(e.target.checked)} />
        Davet e-postası
      </label>
      <button type="button" data-testid={`orphan-link-${row.email}`} disabled={!pid || busy} onClick={link}
        className="inline-flex items-center justify-center gap-1.5 bg-rose-600 hover:bg-rose-700 text-white px-3 py-1.5 rounded-lg font-semibold text-xs disabled:opacity-50">
        <Link2 size={13} /> {busy ? "Bağlanıyor…" : "Bağla"}
      </button>
    </div>
  );
}

export function OrphanAccountsPanel({ onChanged }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/users/orphan-links").then((r) => setRows(r.data.rows || [])).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  if (!rows.length) return null;
  return (
    <div data-testid="orphan-accounts-panel" className="mx-4 md:mx-5 my-3 bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm">
      <div className="flex items-center gap-2 text-rose-800 font-semibold mb-1">
        <Unlink size={15} /> {rows.length} kullanıcı hesabı silinmiş bir personel kaydına bağlı
      </div>
      <p className="text-[11px] text-rose-700 mb-2">Bu hesaplar prim, ekstre ve avans ekranlarında kendi kayıtlarını göremez. İsim benzerliğine göre önerilen personeli kontrol edip bağlayın.</p>
      {rows.map((r) => <OrphanRow key={r.id} row={r} onLinked={() => { load(); onChanged?.(); }} />)}
    </div>
  );
}
